'use client'

import { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import styles from './index.module.scss'

import CardIcon from './card-icon.svg'
import PaypalIcon from './paypal-icon.svg'
import MbWayIcon from './mbway-icon.svg'

import trackGA4 from '@booking/services/trackingGa4'
import { useBookingState } from '@booking/state/booking.context'

type PaymentMethodKey = 'card' | 'paypal' | 'mbway'

type PaymentMethodsProps = {
  method: PaymentMethodKey | null
  onPayWithCard: () => Promise<void> | void
  onPayWithPaypal: () => Promise<void> | void
  onPayWithMbWay: () => Promise<void> | void
}

export default function PaymentMethods({
  method,
  onPayWithCard,
  onPayWithPaypal,
  onPayWithMbWay,
}: PaymentMethodsProps) {
  const t = useTranslations('booking')
  const { cart } = useBookingState()

  const [switching, setSwitching] = useState(false)

  const disabled = Boolean(cart && Number(cart.total) === 0)

  useEffect(() => {
    if (!method) return
    trackGA4.selectPaymentMethod?.(method)
  }, [method])

  const run = useCallback(
    async (m: PaymentMethodKey, fn: () => Promise<void> | void) => {
      if (switching || disabled) return
      setSwitching(true)
      try {
        await fn()
      } finally {
        setSwitching(false)
      }
    },
    [disabled, switching],
  )

  const methods: { key: PaymentMethodKey; icon: React.ReactNode; name: string; sub: string; fn: () => Promise<void> | void }[] = [
    {
      key: 'card',
      icon: <CardIcon width="28" />,
      name: t('ticket.payment.method.card'),
      sub: t('ticket.payment.method.cardSub'),
      fn: onPayWithCard,
    },
    {
      key: 'paypal',
      icon: <PaypalIcon width="18" />,
      name: t('ticket.payment.method.paypal'),
      sub: t('ticket.payment.method.paypalSub'),
      fn: onPayWithPaypal,
    },
    {
      key: 'mbway',
      icon: <MbWayIcon width="26" />,
      name: t('ticket.payment.method.mbway'),
      sub: t('ticket.payment.method.mbwaySub'),
      fn: onPayWithMbWay,
    },
  ]

  return (
    <div className={styles.grid}>
      {methods.map(({ key, icon, name, sub, fn }) => {
        const selected = method === key
        const waiting = switching && selected

        return (
          <button
            key={key}
            type="button"
            data-id={`${key}-payment-button`}
            className={cn(styles.card, selected && styles.cardSelected)}
            disabled={switching || disabled}
            onClick={() => run(key, fn)}
          >
            {selected && !switching && <CheckBadge />}

            <span className={styles.cardIcon}>{icon}</span>

            <span className={styles.cardName}>
              {waiting ? (
                <span className={styles.switching}>{t('ticket.payment.pleaseWait')}</span>
              ) : (
                name
              )}
            </span>

            <span className={styles.cardSub}>{sub}</span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------

function CheckBadge() {
  return (
    <span className={styles.checkBadge}>
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}
